import { readVideoDuration } from '@utils/VideoDuration';

export const MAX_VIDEO_DURATION_SECONDS = 30;
export const MAX_FILE_SIZE_MB = 200;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
export const ACCEPTED_VIDEO_TYPES = '.mp4,.webm,.mov,.ogg,.ogv';
export const ALLOWED_VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/quicktime',
];

/**
 * Validates a user-selected video file against the size / format / duration caps.
 * Returns a user-facing error message, or null when the file is acceptable.
 */
export async function validateVideoFile(file: File): Promise<string | null> {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `File exceeds ${MAX_FILE_SIZE_MB}MB limit`;
  }

  const baseMimeType = (file.type || '').split(';')[0].trim();
  if (!ALLOWED_VIDEO_MIME_TYPES.includes(baseMimeType)) {
    return 'Invalid video format. Please upload MP4, WebM, MOV, or OGG.';
  }

  let seconds: number | null = null;
  try {
    seconds = await readVideoDuration(file);
  } catch {
    // Probe failed — fall through with size as the only block.
  }
  if (seconds !== null && seconds > MAX_VIDEO_DURATION_SECONDS) {
    return `Video exceeds ${MAX_VIDEO_DURATION_SECONDS} seconds (got ${Math.round(seconds)}s).`;
  }

  return null;
}

/**
 * Converts a recorded video Blob into a File with a timestamped name, picking an
 * mp4/webm extension from the blob's MIME type.
 */
export function recordedBlobToFile(blob: Blob, namePrefix: string): File {
  const mimeType = blob.type.split(';')[0] || 'video/webm';
  const extension = mimeType === 'video/mp4' ? 'mp4' : 'webm';
  return new File([blob], `${namePrefix}-${Date.now()}.${extension}`, {
    type: mimeType,
  });
}
